/**
 * Product image serve + failover: respects ImageKit primary preference.
 * Prefer ON: ImageKit → Cloudinary → Blob → placeholder
 * Prefer OFF: Cloudinary → ImageKit → Blob → placeholder
 */

import { productCardImageUrl, productDetailImageUrl } from './cloudinaryImage';
import { pickMediaUrl, mediaFailoverChain, applyMediaFailoverChain } from './mediaUrl';

export const PRODUCT_IMAGE_PLACEHOLDER = '/images/products/electronics.jpg';

export function getPrimaryProductImage(product) {
  if (!product) return '';
  if (product.thumbnail) return String(product.thumbnail).trim();
  if (product.image) return String(product.image).trim();
  if (Array.isArray(product.images) && product.images.length > 0) {
    const idx =
      product.primaryImageIndex != null && product.primaryImageIndex < product.images.length
        ? product.primaryImageIndex
        : 0;
    const raw = product.images[idx];
    return String(typeof raw === 'string' ? raw : raw?.url || raw?.src || '').trim();
  }
  return '';
}

export function getPrimaryImageKitUrl(product) {
  if (!product) return '';
  if (product.thumbnailImagekit) return String(product.thumbnailImagekit).trim();
  if (Array.isArray(product.imagekitImages) && product.imagekitImages.length > 0) {
    const idx =
      product.primaryImageIndex != null &&
      Array.isArray(product.images) &&
      product.primaryImageIndex < product.images.length
        ? product.primaryImageIndex
        : 0;
    return String(product.imagekitImages[idx] || product.imagekitImages[0] || '').trim();
  }
  return '';
}

export function getPrimaryProductImageFallback(product) {
  if (!product) return '';
  if (product.thumbnailFallback) return String(product.thumbnailFallback).trim();
  if (Array.isArray(product.imageFallbacks) && product.imageFallbacks.length > 0) {
    const idx =
      product.primaryImageIndex != null &&
      Array.isArray(product.images) &&
      product.primaryImageIndex < product.images.length
        ? product.primaryImageIndex
        : 0;
    return String(product.imageFallbacks[idx] || product.imageFallbacks[0] || '').trim();
  }
  return '';
}

/** Ordered failover candidates after the primary src fails. */
export function getProductImageFailoverChain(product, { preferImageKit = false } = {}) {
  const primary = getPrimaryProductImage(product);
  const ik = getPrimaryImageKitUrl(product);
  const blob = getPrimaryProductImageFallback(product);
  const raw = mediaFailoverChain({
    primary,
    imagekit: ik,
    blob,
    preferImageKit,
    placeholder: PRODUCT_IMAGE_PLACEHOLDER,
  });
  return raw.map((u) => (u === PRODUCT_IMAGE_PLACEHOLDER || /\.blob\.vercel-storage\.com/i.test(u) ? u : productCardImageUrl(u)));
}

/** Optimized primary for product cards. */
export function resolveProductCardImageSrc(product, { preferImageKit = false } = {}) {
  const primary = getPrimaryProductImage(product);
  const ik = getPrimaryImageKitUrl(product);
  const blob = getPrimaryProductImageFallback(product);
  const chosen = pickMediaUrl({ primary, imagekit: ik, blob, preferImageKit });
  return chosen ? productCardImageUrl(chosen) : '';
}

/** Optimized primary for detail gallery slot. */
export function resolveProductDetailImageSrc(url, { preferImageKit = false, imagekitUrl = '' } = {}) {
  const chosen = pickMediaUrl({
    primary: url,
    imagekit: imagekitUrl,
    preferImageKit,
  });
  return productDetailImageUrl(chosen || url);
}

/**
 * Advance <img> through failover chain on error.
 */
export function applyImageFailoverChain(imgEl, product, { preferImageKit = false } = {}) {
  if (!imgEl) return false;
  const chain = getProductImageFailoverChain(product, { preferImageKit });
  return applyMediaFailoverChain(imgEl, chain);
}

/**
 * @deprecated Prefer applyImageFailoverChain.
 */
export function nextImageSrcOnError(currentSrc, fallbackUrl, placeholder = PRODUCT_IMAGE_PLACEHOLDER) {
  const current = String(currentSrc || '').trim();
  const fallback = String(fallbackUrl || '').trim();
  const place = String(placeholder || PRODUCT_IMAGE_PLACEHOLDER).trim();

  if (fallback && current !== fallback && !current.includes(fallback.split('?')[0])) {
    if (!/\.blob\.vercel-storage\.com/i.test(current) && !/ik\.imagekit\.io/i.test(current)) {
      return fallback;
    }
  }
  if (current !== place) return place;
  return null;
}

export function applyImageFailover(imgEl, fallbackUrl, placeholder = PRODUCT_IMAGE_PLACEHOLDER) {
  if (!imgEl) return false;
  const next = nextImageSrcOnError(imgEl.currentSrc || imgEl.src, fallbackUrl, placeholder);
  if (!next) return false;
  imgEl.src = next;
  return true;
}
